import { Download, Loader2, Presentation } from 'lucide-react';

import { CANVAS_CREDITS } from '@repo/contracts/credits';

import { trackEvent } from '../analytics/posthog';
import { isInsufficientCredits } from '../api/client';
import { useBuildDeck, useReport } from './use-report';

/**
 * The report as a presentation deck: made on request — Floto writes the
 * storyline, the server lays out the slides — then downloaded as .pptx.
 * Charged once per room; downloading it again, or remaking it, is free.
 *
 * `tone` follows where it sits: `solid` beside "Save as PDF" on the report,
 * `quiet` beside "Open report" in the ended room.
 */
export function DeckButton({
  className = '',
  roomId,
  tone = 'solid',
}: {
  className?: string;
  roomId: string;
  tone?: 'quiet' | 'solid';
}) {
  const { data } = useReport(roomId);
  const build = useBuildDeck(roomId);
  const report = data?.report;
  if (!data || report?.status !== 'done') return null;

  const deck = report.deck;
  const building =
    build.isPending ||
    (deck?.status === 'pending' &&
      Date.now() - Date.parse(deck.startedAt) < 5 * 60_000);
  const shape = `flex h-8 shrink-0 items-center gap-1.5 rounded-button px-3 text-ui font-semibold transition ${className}`;
  const look =
    tone === 'solid'
      ? 'bg-brand text-white hover:bg-brand-alt'
      : 'text-text-primary ring-1 ring-hairline hover:bg-surface-hover';

  if (!building && deck?.status === 'done' && data.deckUrl) {
    return (
      <a
        className={`${shape} ${look}`}
        download
        href={data.deckUrl}
        onClick={() => {
          trackEvent('deck_downloaded', { room_id: roomId, source: tone });
        }}
        rel="noopener"
      >
        <Download aria-hidden="true" className="size-3.5" />
        Download deck
      </a>
    );
  }

  const paid = deck?.credits !== undefined;
  const short = isInsufficientCredits(build.error);
  let label: string;
  if (building) label = 'Making your deck…';
  else if (short)
    label = `Needs ${String(CANVAS_CREDITS.RESEARCH_DECK)} credits`;
  else if (build.isError || deck?.status === 'failed')
    label = 'Make the deck again';
  else label = 'Make a deck';

  return (
    <button
      className={`${shape} ${look} cursor-pointer disabled:cursor-default disabled:opacity-60`}
      // The server is the gate: after a top-up elsewhere, asking again works.
      disabled={building}
      onClick={() => {
        trackEvent('deck_requested', {
          paid,
          retry: build.isError || deck?.status === 'failed',
          room_id: roomId,
          source: tone === 'solid' ? 'report' : 'room',
        });
        build.mutate();
      }}
      title={
        paid
          ? 'Already paid for — free to make again'
          : `A presentation of this report, ready to share — ${String(CANVAS_CREDITS.RESEARCH_DECK)} credits`
      }
      type="button"
    >
      {building ? (
        <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
      ) : (
        <Presentation aria-hidden="true" className="size-3.5" />
      )}
      {label}
      {!building && !short && !paid ? (
        <span
          className={`rounded-pill px-1.5 text-tag tabular-nums ${tone === 'solid' ? 'bg-white/20' : 'bg-warm text-text-secondary'}`}
        >
          {CANVAS_CREDITS.RESEARCH_DECK}
        </span>
      ) : null}
    </button>
  );
}
